import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listMoleculeDrawings,
  type MoleculeDrawingArchiveStatus,
  type MoleculeDrawingSummary
} from '@peruselab/api-client/moleculeDrawings';

/** Enough to fill the results area on a large screen before the first scroll. */
export const MY_DRAWINGS_PAGE_SIZE = 24;

export type MyDrawingsFeedState =
  | { readonly state: 'idle' }
  | { readonly state: 'loading' }
  | { readonly state: 'ready' }
  | { readonly state: 'error'; readonly message: string };

export interface MyDrawingsFeed {
  readonly drawings: readonly MoleculeDrawingSummary[];
  readonly totalElements: number | null;
  readonly status: MyDrawingsFeedState;
  readonly hasMore: boolean;
  /** Loads the next page (no-op while one is loading or none is left). */
  readonly loadMore: () => void;
  /** Re-requests the page that failed. */
  readonly retry: () => void;
  /** Starts again from page 0 (#837: after a drawing is restored, it leaves the Archived view). */
  readonly refresh: () => void;
}

// #682 -- "My drawings": the signed-in user's own drawings (newest-updated first, archived ones
// excluded -- the same list the File drawer pages through), shown in the results area while the
// canvas is empty. Page 0 is re-fetched every time the feed becomes active, so a drawing saved in
// the meantime is there when the canvas is cleared again; later pages are appended as the user
// scrolls (see MyDrawingsFeed's sentinel). #837 -- `archiveStatus` 'archived' lists the archived
// drawings instead (the File drawer's Recent drawings list used to be the only way to reach them).
export function useMyDrawingsFeed(
  active: boolean,
  archiveStatus: MoleculeDrawingArchiveStatus = 'active',
  // #1051: changes when the active tenant does -- the feed is that tenant's drawings, reloaded.
  scopeKey = ''
): MyDrawingsFeed {
  const [drawings, setDrawings] = useState<readonly MoleculeDrawingSummary[]>([]);
  const [totalElements, setTotalElements] = useState<number | null>(null);
  const [nextPage, setNextPage] = useState(0);
  const [status, setStatus] = useState<MyDrawingsFeedState>({ state: 'idle' });
  // Guards against a slow page landing after the feed was reset (re-activated) or deactivated.
  const generation = useRef(0);
  const loading = useRef(false);
  const [refreshCount, setRefreshCount] = useState(0);

  const load = useCallback(
    async (page: number, requestGeneration: number) => {
      loading.current = true;
      setStatus({ state: 'loading' });
      try {
        const result = await listMoleculeDrawings(page, MY_DRAWINGS_PAGE_SIZE, '', 'updatedAt', 'desc', archiveStatus);
        if (generation.current !== requestGeneration) return;
        setDrawings((current) => (page === 0 ? result.content : [...current, ...result.content]));
        setTotalElements(result.totalElements);
        setNextPage(page + 1);
        setStatus({ state: 'ready' });
      } catch (error) {
        if (generation.current !== requestGeneration) return;
        setStatus({
          state: 'error',
          message: error instanceof Error ? error.message : 'Could not load your drawings.'
        });
      } finally {
        if (generation.current === requestGeneration) loading.current = false;
      }
    },
    // scopeKey is not read here: it makes a new loader, so the effect below reloads page 0.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [archiveStatus, scopeKey]
  );

  useEffect(() => {
    generation.current += 1;
    loading.current = false;
    if (!active) return;
    const requestGeneration = generation.current;
    // Fetching on activation is this effect's job (syncing with the server); `load` marks the
    // feed as loading before it awaits.
    // eslint-disable-next-line react/set-state-in-effect
    void load(0, requestGeneration);
  }, [active, load, refreshCount]);

  const hasMore = totalElements !== null && drawings.length < totalElements;

  const loadMore = useCallback(() => {
    if (!active || loading.current || !hasMore) return;
    void load(nextPage, generation.current);
  }, [active, hasMore, load, nextPage]);

  const retry = useCallback(() => {
    if (!active || loading.current) return;
    void load(nextPage, generation.current);
  }, [active, load, nextPage]);

  const refresh = useCallback(() => setRefreshCount((count) => count + 1), []);

  return { drawings, totalElements, status, hasMore, loadMore, retry, refresh };
}
